import { ComponentType, useEffect, useState } from 'react';
import { Option, SelectChangeEvent } from '../../hocs';
import DefaultStyledSelect from './DefaultStyledSelect';

export interface CustomSelectProps {
    disabled?: boolean;
    onChange: (event: SelectChangeEvent<string>) => void;
    value?: string;
    options?: Option[];
}

export interface SelectProps extends Omit<CustomSelectProps, 'onChange' | 'value'> {
    options?: Option[];
    name: string;
    onChange: (event: SelectChangeEvent<string>) => void;
    defaultValue?: string;
    value?: string;
    CustomSelect?: ComponentType<CustomSelectProps>;
}

/**
 * Used for CRMIS apps, by default has crmis specific styling and behaviour
 * Styles can be tweaked by using CustomSelect
 * Might be deprecated in future
 */
const Select = ({
    options = [], name, onChange, defaultValue, disabled,
    CustomSelect = DefaultStyledSelect, value,
}: SelectProps): JSX.Element | null => {
    const [selectedValue, setSelectedValue] = useState<string>(defaultValue || value || '');
    // Using custom defaultValue implementation
    // Reason is: regular defaultValue on HTML element, once provided, won't change.
    // With custom defaultValue, you can updated it as long as value is not provided yet.
    // Note:
    // if both value and defaultValue are provided:
    // - on initialisation defaultValue takes precedence
    // - for all other cases, value will take precedence
    // https://reactjs.org/docs/forms.html#the-select-tag

    const handleChange = (event: SelectChangeEvent<string>) => {
        if (onChange) {
            onChange(event)
        }
        setSelectedValue(event.target.value);
    }

    useEffect(() => {
        setSelectedValue(value || defaultValue || '');
        if (defaultValue && onChange) {
            onChange({ target: { value: value || defaultValue } });
        }
        // Should only update on change of defaultValue or value. If passing onChange
        // to dependency array, select won't work: you won't be able to select value
        // This problem is solved in HOCs, but not here. Likely needs to be addressed,
        // if this component is not deprecated in future
        // eslint-disable-next-line
    }, [defaultValue, value]);

    return (
        <>
            <input type='hidden' value={selectedValue} name={name} />
            <CustomSelect onChange={handleChange} disabled={disabled} value={selectedValue} options={options} />
        </>
    );
}

export default Select;
